import { computed } from '@vue/composition-api';
import { LOCAL_STORAGE_KEY, useLocalStorage } from './use-local-storage';
const emptyEntry = {
    answers: [],
    files: [],
    comments: [],
    fileNameMappings: {},
};
export const useSurveyDataCache = (surveyId) => {
    const { setStoredValue, storedValue } = useLocalStorage(LOCAL_STORAGE_KEY.SURVEYS_DATA);
    const ensureCache = () => {
        return (storedValue.value ?? {});
    };
    const hasMeaningfulData = (entry) => {
        const hasAnswers = (entry.answers || []).some(Boolean);
        const hasFiles = (entry.files || []).some(Boolean);
        const hasComments = (entry.comments || []).some(Boolean);
        const hasMappings = entry.fileNameMappings && Object.keys(entry.fileNameMappings).length > 0;
        return hasAnswers || hasFiles || hasComments || hasMappings;
    };
    const PRUNE_TTL_DAYS = 1;
    const MAX_ENTRIES = 10;
    const prune = () => {
        const cache = ensureCache();
        const entries = Object.entries(cache);
        if (entries.length === 0)
            return;
        const now = Date.now();
        const ttlMs = PRUNE_TTL_DAYS * 24 * 60 * 60 * 1000;
        const filtered = entries.filter(([, entry]) => {
            const updatedAt = entry.updatedAt ?? 0;
            // Drop “empty” records created only by opening a survey modal.
            if (!hasMeaningfulData(entry) && updatedAt === 0) {
                return false;
            }
            // Drop records older than TTL.
            if (updatedAt > 0 && now - updatedAt > ttlMs) {
                return false;
            }
            return true;
        });
        filtered.sort((a, b) => (b[1].updatedAt ?? 0) - (a[1].updatedAt ?? 0));
        const limited = filtered.slice(0, MAX_ENTRIES);
        if (limited.length === entries.length)
            return;
        const next = {};
        for (const [id, entry] of limited) {
            next[id] = entry;
        }
        setStoredValue(JSON.stringify(next));
    };
    // One-time cleanup when the composable is used (no UX change).
    prune();
    const setRecord = (entry) => {
        const cache = ensureCache();
        setStoredValue(JSON.stringify({
            ...cache,
            [surveyId]: { ...entry, updatedAt: Date.now() },
        }));
    };
    const surveyCache = computed(() => ensureCache()[surveyId] || { ...emptyEntry });
    const deleteRecord = () => {
        const newCache = { ...ensureCache() };
        delete newCache[surveyId];
        setStoredValue(JSON.stringify(newCache));
    };
    return { surveyCache, setRecord, deleteRecord };
};
